import {
  useMemo,
  useContext,
  useCallback,
  ComponentType,
  Fragment
} from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { useHistory } from 'react-router-dom';
import { Drawer } from 'antd';

import { ReactComponent as ECOLogo } from 'images/ecosu-logo.svg';
import wodomatLogo from 'images/logo-wodomat.webp';
import { userContext } from 'contexts/UserContext';
import { BRANDS, useBrand } from 'hooks/useBrand';
import { ReactComponent as NewIcon } from 'images/new.svg';
import { useBooleanState } from 'hooks/useBooleanState';
import { useBrandFeatures } from 'hooks/useBrandFeatures';
import { Header, Icons } from 'ui-kit';
import { UserMenu } from 'newComponents/UserMenu';
import { SelectLanguage } from 'newComponents/SelectLanguage';
import { MENU } from 'config/menu';
import { useLingui } from '@lingui/react/macro';
import { MenuContext, MenuItem } from './MenuItem';
import styles from './Header.module.scss';

const NewBadge = styled(NewIcon)`
  color: red;
  position: absolute;
  top: -5px;
  right: -25px;
  width: 31px;
`;

const MenuDivider = styled.hr`
  width: 100%;
  margin: 20px 0 10px;
  border: 0;
  border-top: 1px solid rgba(255, 255, 255, 0.25);
`;

const LOGOS: Record<BRANDS, ComponentType> = {
  [BRANDS.AV]: Icons.SmallLogo,
  [BRANDS.ECOSU]: ECOLogo,
  [BRANDS.WODOMAT]: () => (
    <img src={wodomatLogo} className={styles.wodomatLogo} alt="logo" />
  )
};

const MainMenuStyles = createGlobalStyle`
.mainMenu {
  .ant-drawer-wrapper-body{
    background: #222;
  }
  .ant-drawer-header{
    background: transparent;
  }
  .ant-drawer-header-title{
    flex-direction: row-reverse;
    .anticon-close{
      color: #fff;
    }
  }
  .ant-drawer-body {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
  .ant-drawer-header-no-title{
    button {
      color: #fff;
    }
  }
}
`;

function HeaderTT() {
  const [isMenuOpen, shoMenu, hideMenu] = useBooleanState();
  const { user, logout } = useContext(userContext);
  const { t, i18n } = useLingui();
  const history = useHistory();
  const brand = useBrand();

  const goMain = useCallback(() => history.push('/'), [history]);

  const logo = useMemo(() => LOGOS[brand], [brand]);

  const rightMenu = (
    <>
      <UserMenu user={user} onLogout={logout} />
      <SelectLanguage />
    </>
  );

  const features = useBrandFeatures();

  const menuItems = useMemo(
    () =>
      MENU.filter((menuItem) => {
        if (menuItem.feature && !features[menuItem.feature]) return false;
        if (
          menuItem.allowedHosts &&
          !menuItem.allowedHosts.includes(window.location.hostname)
        ) {
          return false;
        }
        return menuItem.showInMainMenu(user);
      }),
    [features, user]
  );

  return (
    <>
      <Header
        rightMenu={rightMenu}
        onMenuClick={shoMenu}
        onLogoClick={goMain}
        logo={logo}
      />
      <MainMenuStyles />
      <Drawer
        visible={isMenuOpen}
        placement="left"
        onClose={hideMenu}
        className="mainMenu"
        width="320px"
      >
        <MenuContext.Provider value={hideMenu}>
          {features.shop && (
            <MenuItem
              href="https://alivewater.ru/onlajn-magazin"
              as="a"
              target="_blank"
            >
              {t`Интернет магазин`}
              <NewBadge />
            </MenuItem>
          )}
          {menuItems.map((menuItem) => {
            const item = menuItem.path.startsWith('http') ? (
              <MenuItem href={menuItem.path}>{i18n._(menuItem.label)}</MenuItem>
            ) : (
              <MenuItem to={menuItem.path} exact={menuItem.path === '/'}>
                {i18n._(menuItem.label)}
              </MenuItem>
            );

            return (
              <Fragment key={menuItem.path}>
                {menuItem.dividerBefore && <MenuDivider />}
                {item}
              </Fragment>
            );
          })}
        </MenuContext.Provider>
      </Drawer>
    </>
  );
}

export default HeaderTT;
